// H04 · Panel de revisión de noticias EN CUARENTENA (ocultas del feed M0).
// Permite ver qué se filtró y por qué, y restaurar en bloque los homónimos
// legítimos. Toda restauración es reversible y marca decisión humana.
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { X, EyeOff, RotateCcw, Loader2, Copy, AlertTriangle, CheckSquare, Square } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";

const TIPO_META = {
  no_sectorial: { label: "Sin relación sectorial", color: "bg-amber-100 text-amber-700", icon: AlertTriangle },
  duplicado: { label: "Duplicadas", color: "bg-sky-100 text-sky-700", icon: Copy },
  manual: { label: "Descartadas a mano", color: "bg-gray-200 text-gray-600", icon: EyeOff },
};

export default function NewsQuarantinePanel({ onClose, onRestored }) {
  const [data, setData] = useState({ items: [], por_tipo: {}, total_cuarentena: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = filter ? `?motivo_tipo=${filter}` : "";
      const { data } = await axios.get(`${API}/competitor-news/quarantined${qs}`);
      setData(data);
      setSel(new Set());
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudieron cargar las noticias ocultas");
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setSel((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const allSelected = data.items.length > 0 && sel.size === data.items.length;
  const toggleAll = () => setSel(allSelected ? new Set() : new Set(data.items.map((i) => i.id)));

  const restoreIds = async (ids) => {
    if (!ids.length) return;
    setBusy(true);
    try {
      const { data: r } = await axios.post(`${API}/competitor-news/restore-bulk`, { ids });
      toast.success(`${r.restauradas} noticia(s) restaurada(s) al feed`);
      onRestored?.();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo restaurar");
    } finally { setBusy(false); }
  };

  const restoreTipo = async (tipo) => {
    setBusy(true);
    try {
      const { data: r } = await axios.post(`${API}/competitor-news/restore-bulk`, { motivo_tipo: tipo });
      toast.success(`${r.restauradas} noticia(s) de "${TIPO_META[tipo]?.label || tipo}" restaurada(s)`);
      onRestored?.();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo restaurar el bloque");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="news-quarantine-panel">
      <div className="bg-white rounded-xl max-w-3xl w-full my-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between sticky top-0 bg-white rounded-t-xl z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
              <EyeOff size={20} className="text-amber-700" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-900">Noticias ocultas del feed · revisión</h3>
              <p className="text-xs text-gray-500 mt-0.5">Las que se filtraron por homónimo o ruido. Restaura las que sí sean del sector; es reversible.</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="nq-close-btn" className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-3">
          {/* Chips de tipo + restaurar bloque */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <button onClick={() => setFilter("")} data-testid="nq-chip-all"
              className={`text-[11px] font-semibold rounded-full px-2.5 py-1 border ${filter === "" ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"}`}>
              Todas ({data.total_cuarentena})
            </button>
            {Object.entries(data.por_tipo || {}).map(([tipo, n]) => {
              if (!n) return null;
              const meta = TIPO_META[tipo] || { label: tipo, color: "bg-gray-100 text-gray-600" };
              return (
                <button key={tipo} onClick={() => setFilter(tipo)} data-testid={`nq-chip-${tipo}`}
                  className={`text-[11px] font-semibold rounded-full px-2.5 py-1 border ${filter === tipo ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"}`}>
                  {meta.label} ({n})
                </button>
              );
            })}
            {filter && (
              <button onClick={() => restoreTipo(filter)} disabled={busy} data-testid="nq-restore-tipo-btn"
                className="ml-auto text-[11px] font-semibold rounded-lg px-2.5 py-1 bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 flex items-center gap-1">
                <RotateCcw size={12} /> Restaurar todo este tipo
              </button>
            )}
          </div>

          {/* Barra selección */}
          {data.items.length > 0 && (
            <div className="flex items-center justify-between border-y border-gray-100 py-2">
              <button onClick={toggleAll} data-testid="nq-select-all" className="text-xs text-gray-600 flex items-center gap-1.5 hover:text-gray-900">
                {allSelected ? <CheckSquare size={14} /> : <Square size={14} />} Seleccionar todo ({data.items.length})
              </button>
              <button onClick={() => restoreIds([...sel])} disabled={busy || sel.size === 0} data-testid="nq-restore-sel-btn"
                className="text-xs font-semibold rounded-lg px-3 py-1.5 bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 flex items-center gap-1.5">
                {busy ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />} Restaurar seleccionadas ({sel.size})
              </button>
            </div>
          )}

          {/* Lista */}
          {loading ? (
            <div className="py-10 text-center text-gray-400"><Loader2 className="animate-spin inline" size={18} /></div>
          ) : data.items.length === 0 ? (
            <p className="text-xs text-gray-400 italic py-8 text-center">No hay noticias ocultas en esta vista.</p>
          ) : (
            <div className="space-y-1.5 max-h-[50vh] overflow-y-auto">
              {data.items.map((n) => {
                const meta = TIPO_META[n.motivo_tipo] || { label: n.motivo_tipo, color: "bg-gray-100 text-gray-600" };
                const checked = sel.has(n.id);
                return (
                  <div key={n.id} onClick={() => toggle(n.id)} data-testid={`nq-item-${n.id}`}
                    className={`flex items-start gap-2 p-2 rounded-lg border cursor-pointer text-xs ${checked ? "border-emerald-300 bg-emerald-50/50" : "border-gray-100 hover:bg-gray-50"}`}>
                    <span className="mt-0.5 shrink-0 text-gray-400">{checked ? <CheckSquare size={14} className="text-emerald-600" /> : <Square size={14} />}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                        <span className="text-[9px] bg-rose-50 text-rose-700 px-1.5 py-0.5 rounded font-bold uppercase">{(n.competitor || "").split(" ")[0]}</span>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded font-semibold ${meta.color}`}>{meta.label}</span>
                      </div>
                      <a href={n.source_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                        className="font-semibold text-gray-800 hover:text-rose-700 line-clamp-2" title={n.title}>{n.title}</a>
                      <p className="text-[10px] text-gray-400 mt-0.5 italic line-clamp-1">{n.relevancia_motivo}</p>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); restoreIds([n.id]); }} disabled={busy} data-testid={`nq-restore-${n.id}`}
                      title="Restaurar al feed" className="shrink-0 text-emerald-600 hover:text-emerald-800 px-1"><RotateCcw size={13} /></button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
